// Any segment with a digit is an identifier: numeric ids, id lists, uuids and
// sensor serials such as `TSS-3498`. No static route segment has one, apart
// from the `v1`/`v2` version prefix. Collapsed to `:id` so Sentry groups one
// issue per route instead of one per row.
const ID_SEGMENT = /\d|^[0-9a-f]{16,}$/i;
const VERSION_SEGMENT = /^v\d+$/i;
// The segment after `file/` is a filename, which can carry customer or site
// names and must not reach an issue title or tag.
const FILE_SEGMENT = 'file';

// Past this length a backend message is noise in an issue title.
const MAX_TITLE_MESSAGE = 200;
// Statuses that one event hits on every route at once: an expired session, or
// a gateway error during a deploy. One issue each, not one per route.
const BUCKETED_STATUSES = [401, 502, 503, 504];
// Axios codes for a request that ran out of time. Per-route: a slow endpoint.
const TIMEOUT_CODES = ['ECONNABORTED', 'ETIMEDOUT'];
export const CANCELED_CODE = 'ERR_CANCELED';

// One namespaced key holds every diagnostic. Bare `status`, `method` and `url`
// would collide with the backend body, which returns `{'status': 'error'}` in
// places: the field would lose its value and drop out of `{...err}`.
const API_META = '__api';

const defineHidden = (target, key, value) => {
  Object.defineProperty(target, key, {
    value, enumerable: false, writable: true, configurable: true
  });
};

/** Request diagnostics for a failed call, or undefined for anything else. */
export const apiMetaOf = error => (error ? error[API_META] : undefined);

export const normalizeEndpoint = (url) => {
  if (!url || typeof url !== 'string') {
    return 'unknown';
  }
  const path = url
    .split('?')[0]
    .split('#')[0]
    .replace(/^[a-z]+:\/\/[^/]+/i, '');

  const segments = path.split('/');
  return segments
    .map((segment, index) => {
      const isId = ID_SEGMENT.test(segment) && !VERSION_SEGMENT.test(segment);
      const isFileName = index > 0 && segments[index - 1] === FILE_SEGMENT && segment !== '';
      return isId || isFileName ? ':id' : segment;
    })
    .join('/') || '/';
};

/**
 * A failed API call. A real Error, so Sentry records a stack and a type instead
 * of "Object captured as promise rejection". The stack alone does not split
 * issues per route: every ApiError is built on one line of axiosResponse.js.
 * The fingerprint in `applyApiErrorGrouping` does that.
 *
 * Keeps the shape callers already read: every backend response field, and
 * `code`, stay own enumerable properties, exactly as the old plain-object
 * rejection had them.
 */
export class ApiError extends Error {
  constructor(message, meta) {
    super(message || '');
    // Babel transpiles `extends Error` down; re-point the prototype so
    // `instanceof ApiError` holds.
    Object.setPrototypeOf(this, ApiError.prototype);

    const info = meta || {};

    if (info.data && typeof info.data === 'object') {
      Object.assign(this, info.data);
    }
    // Error puts `message` on the instance non-enumerable; callers that spread
    // the rejection used to get it, so redefine it as enumerable.
    Object.defineProperty(this, 'message', {
      value: message || '', enumerable: true, writable: true, configurable: true
    });
    this.code = info.status;

    // Non-enumerable: several callers walk Object.keys(err) hunting for backend
    // field errors and must not see request metadata as a field.
    defineHidden(this, API_META, {
      status: info.status,
      method: String(info.method || 'REQUEST').toUpperCase(),
      url: info.url,
      endpoint: normalizeEndpoint(info.url),
      errorCode: info.errorCode
    });

    if (info.stack) {
      defineHidden(this, 'stack', info.stack);
    }
  }
}

// On the prototype, not the instance. A backend `name` field then shadows it
// as an own enumerable property, the way it did before, instead of overwriting
// the error's own name and vanishing from spreads.
ApiError.prototype.name = 'ApiError';

export const isApiError = error => Boolean(apiMetaOf(error));

export const describeApiError = (error) => {
  const meta = apiMetaOf(error) || {};
  return `${meta.method} ${meta.endpoint}`;
};

// A 502 page or a bare-string 404 puts a whole HTML document in `message`.
const titleMessage = (message) => {
  if (!message || typeof message !== 'string') {
    return 'no message';
  }
  const text = message.trim();
  if (text.charAt(0) === '<') {
    return 'non-JSON response';
  }
  return text.length > MAX_TITLE_MESSAGE ? `${text.slice(0, MAX_TITLE_MESSAGE)}…` : text;
};

const classify = (meta) => {
  const route = [meta.method, meta.endpoint];
  if (TIMEOUT_CODES.indexOf(meta.errorCode) !== -1) {
    return { kind: 'timeout', fingerprint: ['api-error', 'timeout', ...route] };
  }
  if (!meta.status) {
    // Offline, tab closed, proxy: the browser never reached us.
    return { kind: 'network', fingerprint: ['api-error', 'network'] };
  }
  if (BUCKETED_STATUSES.indexOf(meta.status) !== -1) {
    return { kind: 'status', fingerprint: ['api-error', String(meta.status)] };
  }
  return { kind: 'route', fingerprint: ['api-error', ...route, String(meta.status)] };
};

/**
 * Sentry `beforeSend`. Sentry groups on the stacktrace, and every ApiError is
 * built on the same line of axiosResponse.js, so without an explicit
 * fingerprint they would all still land in one issue.
 */
export const applyApiErrorGrouping = (event, hint) => {
  const original = hint && hint.originalException;
  const meta = apiMetaOf(original);
  if (!meta) {
    return event;
  }
  // A request the app aborted on purpose, such as a superseded page load.
  if (meta.errorCode === CANCELED_CODE) {
    return null;
  }

  const route = describeApiError(original);
  const { kind, fingerprint } = classify(meta);
  event.fingerprint = fingerprint;

  // Bucketed issues keep the per-route breakdown in these tags.
  event.tags = {
    ...(event.tags || {}),
    api_method: meta.method,
    api_endpoint: meta.endpoint,
    api_status: String(meta.status || 0),
    ...(meta.errorCode ? { api_error_code: meta.errorCode } : {})
  };

  const values = event.exception && event.exception.values;
  if (values && values.length) {
    const value = values[values.length - 1];
    if (kind === 'timeout') {
      value.type = 'ApiTimeout';
      value.value = `${route} timed out`;
    } else if (kind === 'network') {
      value.type = 'ApiNetworkError';
      value.value = `Could not reach ${route}`;
    } else {
      value.type = `ApiError ${meta.status}`;
      value.value = `${route} failed: ${titleMessage(original.message)}`;
    }
  }

  return event;
};
